import React, { useState, useEffect, useMemo } from 'react';
import { useMutation } from 'react-query';
import debounce from 'lodash/debounce';
import { useAppSelector } from '../../../hooks/use-app-selector';
import {
  CheckOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
  DisconnectOutlined,
  PercentageOutlined,
  QuestionCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Row,
  Col,
  Typography,
  Divider,
  Button,
  InputNumber,
  Tooltip,
  Select,
  Space,
  Flex,
  Switch,
  Tag,
  Spin,
} from 'antd';
import styled from 'styled-components';
import { t } from '../../../utils/i18n';
import { useGlobals } from '../../../globals-context';
import useMerchantSettings from '../../../hooks/use-merchant-settings';
import {
  useDisablePayPalIntegration,
  useFetchSubPaymentMethods,
  useToggleSubPaymentMethod,
} from '../../../shared/hooks/payments';
import { useUpdateBookingConfigurations } from '../../../shared/hooks/booking-configurations';
import {
  PaymentAvailabilityType,
  SubPaymentMethodName,
} from '../../../shared/types';
import type { IPaymentMethod } from '@shore/types';
import createQueryConfig from '../../../queries';
import { BRAND_ICON_MAP } from './PaymentBrandIcons';

interface SetupDownPaymentsParams {
  percentage: number;
}

interface PaymentMethodMeta {
  down_payment_percentage?: number;
}

interface SectionPaymentPolicyProps {
  currentPaymentAvailability: PaymentAvailabilityType;
  onPaymentPolicyChange: (value: PaymentAvailabilityType) => void;
}

interface SectionPrepaymentAmountProps {
  amount: number;
  onAmountChange: (value: number) => void;
  disabled: boolean;
}

interface ShorePayOnlineCardProps {
  merchantId: string;
}

interface PayPalCardProps {
  merchantId: string;
}

interface SectionPaymentGatewayProps {
  merchantId: string;
  isPayPalFeatureToggleEnabled: boolean;
  paymentMethods: IPaymentMethod[];
}

const StyledPaymentSetupPageWrapper = styled.div`
  width: 100%;
  max-width: 650px;
  padding: 0 24px 24px;
`;

const StyledGatewayCard = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
`;

const StyledGatewayCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
`;

const StyledSubPaymentMethodRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
`;

const PAYMENT_POLICY_OPTIONS = [
  {
    value: 'disabled' as PaymentAvailabilityType,
    label: t(
      'application-settings.payments.setup-page.section-booking-payment.payment-types.pay-at-location'
    ),
    icon: <CloseCircleOutlined />,
    description: t(
      'application-settings.payments.setup-page.section-payment-policy.helper.disabled'
    ),
  },
  {
    value: 'mandatory' as PaymentAvailabilityType,
    label: t(
      'application-settings.payments.setup-page.section-booking-payment.payment-types.mandatory'
    ),
    icon: <CreditCardOutlined />,
    description: t(
      'application-settings.payments.setup-page.section-payment-policy.helper.mandatory'
    ),
  },
  {
    value: 'optional' as PaymentAvailabilityType,
    label: t(
      'application-settings.payments.setup-page.section-booking-payment.payment-types.optional'
    ),
    icon: <UserOutlined />,
    description: t(
      'application-settings.payments.setup-page.section-payment-policy.helper.optional'
    ),
  },
];

// ── Section: Payment Policy ──────────────────────────────────────────

function SectionPaymentPolicy({
  currentPaymentAvailability,
  onPaymentPolicyChange,
}: SectionPaymentPolicyProps) {
  const selectedOption = PAYMENT_POLICY_OPTIONS.find(
    (o) => o.value === currentPaymentAvailability
  )!;

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Typography.Title level={3} style={{ marginTop: 16, marginBottom: 4 }}>
          {t(
            'application-settings.payments.setup-page.section-payment-policy.title'
          )}
        </Typography.Title>
        <Typography.Text
          type="secondary"
          style={{ display: 'block', maxWidth: 500 }}
        >
          {t(
            'application-settings.payments.setup-page.section-payment-policy.description'
          )}
        </Typography.Text>
      </Col>
      <Col span={24} style={{ maxWidth: 500 }}>
        <Select
          value={currentPaymentAvailability}
          onChange={onPaymentPolicyChange}
          style={{ width: '100%' }}
          size="large"
          labelRender={() => (
            <Space>
              {selectedOption.icon}
              {selectedOption.label}
            </Space>
          )}
          optionRender={(option) => {
            const { icon, label, description } = option.data;
            const isSelected = option.value === currentPaymentAvailability;
            return (
              <Flex gap={8} align="center" style={{ fontWeight: 'normal' }}>
                <div>{icon}</div>
                <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                  <Typography.Text>{label}</Typography.Text>
                  <Typography.Text
                    type="secondary"
                    style={{ whiteSpace: 'normal' }}
                  >
                    {description}
                  </Typography.Text>
                </Flex>
                {isSelected && <CheckOutlined />}
              </Flex>
            );
          }}
          options={PAYMENT_POLICY_OPTIONS}
        />
        <Typography.Text
          type="secondary"
          style={{ display: 'block', marginTop: 8 }}
        >
          {selectedOption.description}
        </Typography.Text>
      </Col>
    </Row>
  );
}

// ── Section: Prepayment Amount ───────────────────────────────────────

function SectionPrepaymentAmount({
  amount,
  onAmountChange,
  disabled,
}: SectionPrepaymentAmountProps) {
  return (
    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
      <Col span={24} style={{ maxWidth: 500 }}>
        <Flex align="center" gap={4} style={{ marginBottom: 4 }}>
          <Typography.Text>
            {t(
              'application-settings.payments.setup-page.section-payment-policy.prepayment-amount.label'
            )}
          </Typography.Text>
          <Tooltip
            title={t(
              'application-settings.payments.setup-page.section-payment-policy.prepayment-amount.tooltip'
            )}
          >
            <QuestionCircleOutlined style={{ color: '#bdc3c7' }} />
          </Tooltip>
        </Flex>
        <InputNumber
          size="large"
          min={1}
          max={100}
          value={amount}
          onChange={(val) => val !== null && onAmountChange(val)}
          prefix={<PercentageOutlined />}
          style={{ width: '100%' }}
          disabled={disabled}
        />
      </Col>
    </Row>
  );
}

// ── Shore Pay Online Card ────────────────────────────────────────────

function ShorePayOnlineCard({ merchantId }: ShorePayOnlineCardProps) {
  const {
    data: subPaymentMethodsResponse,
    isLoading: isLoadingSubPaymentMethods,
  } = useFetchSubPaymentMethods(merchantId);

  const { mutate: mutateToggleSubPaymentMethod } = useToggleSubPaymentMethod();

  const subPaymentMethods = subPaymentMethodsResponse?.data ?? [];

  const handleToggle = (
    name: SubPaymentMethodName,
    currentEnabled: boolean
  ) => {
    mutateToggleSubPaymentMethod({
      merchantId,
      subPaymentMethodName: name,
      enabled: !currentEnabled,
    });
  };

  return (
    <StyledGatewayCard>
      <StyledGatewayCardHeader>
        <Flex align="center" gap={8}>
          <Typography.Text strong style={{ fontSize: 14 }}>
            Shore Pay Online
          </Typography.Text>
          <Tag color="green" bordered>
            {t(
              'application-settings.payments.setup-page.section-payment-gateways.status.active'
            )}
          </Tag>
        </Flex>
      </StyledGatewayCardHeader>
      <Divider style={{ margin: 0 }} />
      {isLoadingSubPaymentMethods ? (
        <Flex justify="center" style={{ padding: 24 }}>
          <Spin />
        </Flex>
      ) : (
        subPaymentMethods.map((method, index) => (
          <React.Fragment key={method.name}>
            <StyledSubPaymentMethodRow>
              <Flex align="center" gap={8}>
                <Typography.Text>{method.displayName}</Typography.Text>
                <Flex gap={4}>
                  {method.icons.map((iconKey) => {
                    const IconComponent = BRAND_ICON_MAP[iconKey];
                    return IconComponent ? (
                      <IconComponent key={iconKey} />
                    ) : null;
                  })}
                </Flex>
              </Flex>
              <Switch
                checked={method.enabled}
                onChange={() => handleToggle(method.name, method.enabled)}
              />
            </StyledSubPaymentMethodRow>
            {index < subPaymentMethods.length - 1 && (
              <Divider style={{ margin: 0 }} />
            )}
          </React.Fragment>
        ))
      )}
    </StyledGatewayCard>
  );
}

// ── PayPal Card ──────────────────────────────────────────────────────

function PayPalCard({ merchantId }: PayPalCardProps) {
  const { trackMethod } = useGlobals()!;

  const {
    mutate: mutateDisablePaypalIntegration,
    isLoading: isLoadingDisablePaypalIntegration,
  } = useDisablePayPalIntegration();

  const handleDisconnect = () => {
    trackMethod(
      'application-settings-app_online_payment_settings_screen_disable_paypal_modal_opened'
    );
    mutateDisablePaypalIntegration({ merchantId });
  };

  return (
    <StyledGatewayCard>
      <StyledGatewayCardHeader>
        <Flex align="center" gap={8}>
          <Typography.Text strong style={{ fontSize: 14 }}>
            PayPal
          </Typography.Text>
          <Tag color="green" bordered>
            {t(
              'application-settings.payments.setup-page.section-payment-gateways.status.active'
            )}
          </Tag>
        </Flex>
        <Button
          icon={<DisconnectOutlined />}
          loading={isLoadingDisablePaypalIntegration}
          onClick={handleDisconnect}
        >
          {t(
            'application-settings.payments.setup-page.section-payment-gateways.status.disconnect'
          )}
        </Button>
      </StyledGatewayCardHeader>
      <Divider style={{ margin: 0 }} />
      <div style={{ padding: 16 }}>
        <Typography.Text type="secondary">
          {t(
            'application-settings.payments.setup-page.section-payment-gateways.paypal-description'
          )}
        </Typography.Text>
      </div>
    </StyledGatewayCard>
  );
}

// ── Section: Payment Gateways ────────────────────────────────────────

function SectionPaymentGateway({
  merchantId,
  paymentMethods,
  isPayPalFeatureToggleEnabled,
}: SectionPaymentGatewayProps) {
  const tooltipText = isPayPalFeatureToggleEnabled
    ? t(
        'application-settings.payments.setup-page.section-payment-gateways.title-tooltip'
      )
    : t(
        'application-settings.payments.setup-page.section-payment-gateways.title-tooltip-for-stripe-only'
      );

  const shorePayOnline = paymentMethods.find(
    (m) => m.name === 'shorepay_online'
  );
  const paypal = paymentMethods.find((m) => m.name === 'paypal');

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Typography.Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>
          {t(
            'application-settings.payments.setup-page.section-payment-gateways.title'
          )}
          <Tooltip title={tooltipText}>
            <QuestionCircleOutlined
              style={{
                fontSize: 18,
                marginLeft: 8,
                color: '#bdc3c7',
              }}
            />
          </Tooltip>
        </Typography.Title>
        <Typography.Text
          type="secondary"
          style={{ display: 'block', maxWidth: 500 }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: t(
                'application-settings.payments.setup-page.section-payment-gateways.description'
              ),
            }}
          />
        </Typography.Text>
      </Col>
      <Col span={24}>
        <Flex vertical gap={16} style={{ maxWidth: 500 }}>
          {shorePayOnline && <ShorePayOnlineCard merchantId={merchantId} />}
          {paypal && isPayPalFeatureToggleEnabled && (
            <PayPalCard merchantId={merchantId} />
          )}
        </Flex>
      </Col>
    </Row>
  );
}

// ── Main Component ───────────────────────────────────────────────────

export function PaymentSetupPageV2() {
  const { queryClient, apiClient } = useGlobals()!;
  const queryConfig = createQueryConfig(queryClient, apiClient);

  const { merchantSettings } = useMerchantSettings();
  const { merchantId, featureToggles } = merchantSettings || {};

  const bookingConfigs = useAppSelector((state) => state.bookingConfigs);
  const currentMerchant = useAppSelector((state) => state.currentMerchant);

  const { mutate: mutateUpdateBookingConfigurations } =
    useUpdateBookingConfigurations();

  const { mutate: mutateSetupDownPayments } = useMutation<
    unknown,
    unknown,
    SetupDownPaymentsParams
  >(queryConfig.downPayments.setupDownPayments({ merchantId }));

  const { mutate: mutateDisableDownPayments } = useMutation<
    unknown,
    unknown,
    void
  >(queryConfig.downPayments.disableDownPayments({ merchantId }));

  const paymentMethods = currentMerchant.paymentSettings?.paymentMethods ?? [];

  const shorePayOnlineMeta = (
    paymentMethods.find((m) => m.name === 'shorepay_online') as any
  )?.meta as PaymentMethodMeta | undefined;

  const [prepaymentAmount, setPrepaymentAmount] = useState<number>(
    shorePayOnlineMeta?.down_payment_percentage ?? 100
  );

  useEffect(() => {
    if (shorePayOnlineMeta?.down_payment_percentage !== undefined) {
      setPrepaymentAmount(shorePayOnlineMeta.down_payment_percentage);
    }
  }, [shorePayOnlineMeta?.down_payment_percentage]);

  const debouncedSetupDownPayments = useMemo(
    () =>
      debounce((params: SetupDownPaymentsParams) => {
        mutateSetupDownPayments(params);
      }, 500),
    [mutateSetupDownPayments]
  );

  const isPayPalFeatureToggleEnabled =
    featureToggles?.paypal_payment_enabled || false;

  const handleOnPaymentPolicyChange = (value: PaymentAvailabilityType) => {
    mutateUpdateBookingConfigurations({
      merchantId,
      paymentAvailability: value,
    });

    if (value === 'mandatory') {
      const amount = prepaymentAmount || 10;
      setPrepaymentAmount(amount);
      mutateSetupDownPayments({ percentage: amount });
    } else if (value === 'disabled') {
      mutateDisableDownPayments();
    }
  };

  const handlePrepaymentAmountChange = (value: number) => {
    setPrepaymentAmount(value);
    debouncedSetupDownPayments({ percentage: value });
  };

  return (
    <StyledPaymentSetupPageWrapper>
      <SectionPaymentPolicy
        currentPaymentAvailability={
          bookingConfigs?.customerPayment || 'disabled'
        }
        onPaymentPolicyChange={handleOnPaymentPolicyChange}
      />
      {bookingConfigs.customerPayment !== 'disabled' && (
        <SectionPrepaymentAmount
          amount={
            bookingConfigs.customerPayment === 'optional'
              ? 100
              : prepaymentAmount
          }
          onAmountChange={handlePrepaymentAmountChange}
          disabled={bookingConfigs.customerPayment === 'optional'}
        />
      )}
      <Divider />
      <SectionPaymentGateway
        merchantId={merchantId}
        paymentMethods={paymentMethods}
        isPayPalFeatureToggleEnabled={isPayPalFeatureToggleEnabled}
      />
    </StyledPaymentSetupPageWrapper>
  );
}
